import React, { useState } from 'react';
import { Plus } from 'lucide-react';
import { useApp } from '../../context/AppContext';
import Modal from '../../components/common/Modal';
import Badge from '../../components/common/Badge';
import { formatDate, generateId } from '../../utils/helpers';
import DateInput from '../../components/common/DateInput';

function IssueReturn() {
  const { bookIssues, books, students, staff, saveBookIssue, updateBookIssue, updateBook } = useApp();
  const [modal, setModal] = useState(false);
  const [form, setForm] = useState({ bookId:'', borrowerId:'', borrowerType:'student', issueDate:new Date().toISOString().split('T')[0], dueDate:'' });

  const getBorrowerName = (issue) => {
    if (issue.borrowerType === 'student') {
      const s = students.find(s=>s.id===issue.borrowerId);
      return s ? `${s.firstName} ${s.lastName}` : '-';
    }
    const st = staff.find(s=>s.id===issue.borrowerId);
    return st ? `${st.firstName} ${st.lastName}` : '-';
  };
  const getBookTitle = (id) => books.find(b=>b.id===id)?.title || '-';

  const getStatus = (issue) => {
    if (issue.returnDate) return 'returned';
    const due = new Date(issue.dueDate);
    const today = new Date();
    return today > due ? 'overdue' : 'issued';
  };

  const returnBook = async (issue) => {
    const today = new Date().toISOString().split('T')[0];
    const dueDate = new Date(issue.dueDate);
    const returnDateObj = new Date();
    const diffDays = Math.max(0, Math.ceil((returnDateObj-dueDate)/(1000*60*60*24)));
    const fine = diffDays * 5;
    const book = books.find(b => b.id === issue.bookId);
    await updateBookIssue(issue.id, {...issue, returnDate:today, fine, status:'returned'});
    if (book) await updateBook(book.id, {...book, availableCopies: book.availableCopies + 1});
  };

  const issueBook = async () => {
    const book = books.find(b=>b.id===form.bookId);
    if (!book || book.availableCopies < 1) { alert('Book not available'); return; }
    const newIssue = { ...form, id:generateId('ISS'), returnDate:null, fine:0, status:'issued' };
    await saveBookIssue(newIssue);
    await updateBook(book.id, {...book, availableCopies: book.availableCopies - 1});
    setModal(false);
  };

  const activeIssues = bookIssues.map(i=>({...i, computedStatus:getStatus(i)}));

  return (
    <div>
      <div className="flex items-center justify-between mb-6">
        <div><h1 className="text-2xl font-bold">Issue / Return</h1><p className="text-gray-500 text-sm">{bookIssues.filter(i=>!i.returnDate).length} books currently issued</p></div>
        <button onClick={()=>setModal(true)} className="flex items-center gap-2 bg-purple-600 hover:bg-purple-700 text-white px-4 py-1 rounded-lg text-sm font-medium"><Plus size={16}/>Issue Book</button>
      </div>
      <div className="bg-white rounded-xl shadow-sm border border-gray-100 overflow-x-auto">
        <table className="w-full text-sm">
          <thead>
            <tr className="bg-gray-50 border-b border-gray-100">
              <th className="px-4 py-1 text-left font-semibold">Book</th>
              <th className="px-4 py-1 text-left font-semibold">Borrower</th>
              <th className="px-4 py-1 text-center font-semibold">Issue Date</th>
              <th className="px-4 py-1 text-center font-semibold">Due Date</th>
              <th className="px-4 py-1 text-center font-semibold">Return Date</th>
              <th className="px-4 py-1 text-center font-semibold">Fine</th>
              <th className="px-4 py-1 text-center font-semibold">Status</th>
              <th className="px-4 py-1 text-center font-semibold">Action</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-50">
            {activeIssues.length === 0 && (
              <tr><td colSpan="8" className="py-10 text-center">No book issues recorded</td></tr>
            )}
            {activeIssues.map(issue=>(
              <tr key={issue.id} className="hover:bg-gray-50">
                <td className="px-4 py-1 font-medium">{getBookTitle(issue.bookId)}</td>
                <td className="px-4 py-1">{getBorrowerName(issue)}<span className="ml-1 capitalize">({issue.borrowerType})</span></td>
                <td className="px-4 py-1 text-center">{formatDate(issue.issueDate)}</td>
                <td className="px-4 py-1 text-center">{formatDate(issue.dueDate)}</td>
                <td className="px-4 py-1 text-center">{issue.returnDate?formatDate(issue.returnDate):'-'}</td>
                <td className="px-4 py-1 text-center"><span className={issue.fine>0?'text-red-600 font-medium':'text-gray-400'}>₹{issue.fine||0}</span></td>
                <td className="px-4 py-1 text-center"><Badge value={issue.computedStatus}/></td>
                <td className="px-4 py-1 text-center">
                  {!issue.returnDate && <button onClick={()=>returnBook(issue)} className="bg-green-600 text-white px-3 py-1 rounded-lg hover:bg-green-700">Return</button>}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <Modal isOpen={modal} onClose={()=>setModal(false)} title="Issue Book">
        <div className="space-y-3">
          <div>
            <label className="block text-sm font-medium mb-1">Book</label>
            <select value={form.bookId||''} onChange={e=>setForm(f=>({...f,bookId:e.target.value}))} className="w-full px-3 py-1 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-purple-300">
              <option value="">Select Book</option>
              {books.filter(b=>b.availableCopies>0).map(b=><option key={b.id} value={b.id}>{b.title} (Available: {b.availableCopies})</option>)}
            </select>
          </div>
          <div>
            <label className="block text-sm font-medium mb-1">Borrower Type</label>
            <select value={form.borrowerType||'student'} onChange={e=>setForm(f=>({...f,borrowerType:e.target.value,borrowerId:''}))} className="w-full px-3 py-1 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-purple-300">
              <option value="student">Student</option>
              <option value="staff">Staff</option>
            </select>
          </div>
          <div>
            <label className="block text-sm font-medium mb-1">{form.borrowerType==='student'?'Student':'Staff'}</label>
            <select value={form.borrowerId||''} onChange={e=>setForm(f=>({...f,borrowerId:e.target.value}))} className="w-full px-3 py-1 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-purple-300">
              <option value="">Select</option>
              {(form.borrowerType==='student'?students.filter(s=>s.status==='active'):staff.filter(s=>s.status==='active')).map(p=><option key={p.id} value={p.id}>{p.firstName} {p.lastName}</option>)}
            </select>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="block text-sm font-medium mb-1">Issue Date</label>
              <DateInput value={form.issueDate||''} onChange={e=>setForm(f=>({...f,issueDate:e.target.value}))} className="w-full px-3 py-1 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-purple-300" />
            </div>
            <div>
              <label className="block text-sm font-medium mb-1">Due Date</label>
              <DateInput value={form.dueDate||''} onChange={e=>setForm(f=>({...f,dueDate:e.target.value}))} className="w-full px-3 py-1 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-purple-300" />
            </div>
          </div>
        </div>
        <div className="flex justify-end gap-2 mt-4 pt-4 border-t">
          <button onClick={()=>setModal(false)} className="px-4 py-1 text-sm hover:bg-gray-100 rounded-lg">Cancel</button>
          <button onClick={issueBook} className="px-4 py-1 text-sm bg-purple-600 text-white rounded-lg hover:bg-purple-700">Issue</button>
        </div>
      </Modal>
    </div>
  );
}
export default IssueReturn;
